<script setup lang="ts">import { computed as _computed } from 'vue';

import SIcon from '@/components/SIcon.vue';
import SRoundedIcon from '@/components/SRoundedIcon/SRoundedIcon.vue';

export type SEmptyStateSize = 'small' | 'medium' | 'large';

interface SEmptyStateProps {
  /**
   * Main title
   */
  title: string;
  /**
   * Description, displayed below the title
   */
  description?: string;
  /**
   * Size of the empty state: small, medium or large
   */
  size?: SEmptyStateSize;
  /**
   * Main icon
   */
  icon?: string;
  /**
   * Primary action button label
   */
  primaryActionLabel?: string,
  /**
   * Primary action button icon
   */
  primaryActionIcon?: string,
  /**
   * Primary action button handler method
   */
  primaryActionHandler?: (() => void) | null;
  /**
   * Secondary action button label
   */
  secondaryActionLabel?: string,
  /**
   * Secondary action button icon
   */
  secondaryActionIcon?: string,
  /**
   * Secondary action button handler method
   */
  secondaryActionHandler?: (() => void) | null;
  /**
   * Learn more link
   */
  learnMoreLink?: string;
  /**
   * Learn more text
   */
  learnMoreText?: string;
}

const props = defineProps({
  title: null,
  description: { default: '' },
  size: { default: 'medium' },
  icon: { default: 'icon-building' },
  primaryActionLabel: { default: '' },
  primaryActionIcon: { default: '' },
  primaryActionHandler: { type: Function, default: null },
  secondaryActionLabel: { default: '' },
  secondaryActionIcon: { default: '' },
  secondaryActionHandler: { type: Function, default: null },
  learnMoreLink: { default: '' },
  learnMoreText: { default: '' }
});

type SEmptyStateSizeConfiguration = {
  iconSize: 'small' | 'medium' | 'large' | 'x-large' | 'xx-large',
  titleClasses: string,
  descriptionClasses: string,
};
const sizeConfiguration = _computed((): SEmptyStateSizeConfiguration => {
  const availableSizeConfigs: { small: SEmptyStateSizeConfiguration, medium: SEmptyStateSizeConfiguration, large: SEmptyStateSizeConfiguration } = {
    small: {
      iconSize: 'small',
      titleClasses: '',
      descriptionClasses: '',
    },
    medium: {
      iconSize: 'large',
      titleClasses: 'text-h6',
      descriptionClasses: '',
    },
    large: {
      iconSize: 'xx-large',
      titleClasses: 'text-h5',
      descriptionClasses: 'text-h6',
    },
  };
  return availableSizeConfigs[props.size];
});

</script>

<template lang="pug">
.s-empty-state.d-flex.flex-column.align-center.justify-center.full-width( :class="size" )
  span.s-empty-state-icon.d-flex.align-center.justify-center
    SRoundedIcon(
      :icon="icon"
      color="iconNeutral"
      :size="sizeConfiguration.iconSize"
      border-color="bgTertiary"
    )
  span.font-weight-medium.text--primary.mt-2( :class="sizeConfiguration.titleClasses" ) {{ title }}
  slot(name="description")
    span.mt-1.grey--text.text-center(v-if="description" :class="sizeConfiguration.descriptionClasses") {{ description }}
    .mt-1.d-flex.align-center(v-if="learnMoreText && learnMoreLink")
      SIcon.mr-1(
        icon="icon-new-window"
        size="small"
        color="iconInfo"
      )
      a.link(:href="learnMoreLink" target="_blank") {{ learnMoreText }}
  .mt-4
    slot(name="actions")
      SButton.mr-1(
        v-if="secondaryActionHandler && secondaryActionLabel"
        :icon="secondaryActionIcon"
        :text="secondaryActionLabel"
        color="secondary"
        variant="outlined"
        @click="secondaryActionHandler"
      )
      SButton(
        v-if="primaryActionHandler && primaryActionLabel"
        :icon="primaryActionIcon"
        :text="primaryActionLabel"
        color="primary"
        @click="primaryActionHandler"
        data-track="primary-action-button"
      )
</template>

<style lang="scss" scoped>
.s-empty-state {
  &.small {
    .s-empty-state-icon {
      width: 32px;
      height: 32px;
    }
  }
  &.medium {
    .s-empty-state-icon {
      width: 48px;
      height: 48px;
    }
  }
  &.large {
    .s-empty-state-icon {
      width: 64px;
      height: 64px;
    }
  }
}
</style>
